import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { apiUrl } from "../../config/api";

import "./admin_live_monitor.css";

const REFRESH_INTERVAL_MS = 60000;
const LOG_PAGE_SIZE = 25;
const MAX_INITIAL_PAGES = 200;
const MAX_LOGS_IN_MEMORY = 500;
const WINDOW_MS = 24 * 60 * 60 * 1000;
const LIVE_MONITOR_PATH = "/admin/live-monitor";
const LEGACY_LOGS_PATH = "/admin/logs";

function formatDate(dateString) {
  if (!dateString) return "-";

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}

function normalizeText(value) {
  return String(value || "").toLowerCase();
}

function getApiName(log) {
  return log.api_name || log.ApiName || log.Api || "Sin API";
}

function getProvider(log) {
  return log.provider_name || log.Provider || log.Supplier || log.SupplierName || "Sin proveedor";
}

function getCompany(log) {
  return log.company_name || log.Company || log.client_name || log.Email || "Sin empresa";
}

function getStatusType(status) {
  const value = normalizeText(status);
  if (value.includes("timeout") || value.includes("scorce_timeout")) return "timeout";
  if (value.includes("success") || value.includes("ok") || value === "200" || value.includes("200")) return "success";
  if (value === "400" || value.includes("400") || value.includes("404") || value.includes("409") || value.includes("422") || value.includes("429")) return "rejected";
  if (value.includes("error") || value.includes("fail") || value.includes("invalid")) return "error";
  return "unknown";
}

function getLogId(log) {
  return log.ApiLogId || log.RequestId || log.id || `${log.EndPointId || "log"}-${log.CreationDate || Math.random()}`;
}

function getWindowStart() {
  return new Date(Date.now() - WINDOW_MS);
}

function isInsideWindow(log, windowStart) {
  const date = new Date(log.CreationDate || 0);
  return !Number.isNaN(date.getTime()) && date >= windowStart;
}

function mergeAndSortLogs(incomingLogs, currentLogs, windowStart) {
  const byId = new Map();

  [...incomingLogs, ...currentLogs].forEach((log) => {
    if (isInsideWindow(log, windowStart)) {
      byId.set(getLogId(log), log);
    }
  });

  return Array.from(byId.values())
    .sort((a, b) => new Date(b.CreationDate || 0) - new Date(a.CreationDate || 0))
    .slice(0, MAX_LOGS_IN_MEMORY);
}

function trackCountedLogs(logs, countedLogIds, windowStart) {
  logs.forEach((log) => {
    if (isInsideWindow(log, windowStart)) {
      countedLogIds.set(getLogId(log), log.CreationDate);
    }
  });

  Array.from(countedLogIds.entries()).forEach(([logId, creationDate]) => {
    const date = new Date(creationDate || 0);
    if (Number.isNaN(date.getTime()) || date < windowStart) {
      countedLogIds.delete(logId);
    }
  });

  return countedLogIds.size;
}

function MetricCard({ label, value, detail, tone }) {
  return (
    <div className={`live_monitor_metric ${tone || ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function AdminLiveMonitor() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [lastSync, setLastSync] = useState("");
  const [initialLimitReached, setInitialLimitReached] = useState(false);
  const [totalCalls24h, setTotalCalls24h] = useState(0);
  const [serverSummary, setServerSummary] = useState(null);
  const [serverProviders, setServerProviders] = useState([]);
  const [serverStatusBreakdown, setServerStatusBreakdown] = useState([]);
  const [serverEndpoints, setServerEndpoints] = useState([]);
  const countedLogIdsRef = useRef(new Map());

  const loadOptimizedMonitor = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    setError("");
    setInitialLimitReached(false);

    try {
      const response = await axios.post(apiUrl(LIVE_MONITOR_PATH), { limit: MAX_LOGS_IN_MEMORY });
      const data = response.data || {};
      const incomingLogs = Array.isArray(data.logs) ? data.logs : [];
      const windowStart = data.startDate ? new Date(data.startDate) : getWindowStart();

      setServerSummary(data.summary || null);
      setServerProviders(Array.isArray(data.providers) ? data.providers : []);
      setServerStatusBreakdown(Array.isArray(data.statusBreakdown) ? data.statusBreakdown : []);
      setServerEndpoints(Array.isArray(data.endpoints) ? data.endpoints : []);
      setTotalCalls24h(Number(data.summary?.total_calls || incomingLogs.length || 0));
      setLogs(mergeAndSortLogs(incomingLogs, [], windowStart));
      setLastSync(formatDate(new Date().toISOString()));
      return true;
    } catch (err) {
      setServerSummary(null);
      setServerProviders([]);
      setServerStatusBreakdown([]);
      setServerEndpoints([]);
      return false;
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadRecentLogs = useCallback(async (silent = false) => {
    const optimizedLoaded = await loadOptimizedMonitor(silent);
    if (optimizedLoaded) return;

    if (!silent) setLoading(true);
    setRefreshing(true);
    setError("");

    try {
      const response = await axios.post(apiUrl(LEGACY_LOGS_PATH), { offset: 0 });
      const incomingLogs = Array.isArray(response.data?.logs) ? response.data.logs : [];
      const windowStart = getWindowStart();
      const totalCount = trackCountedLogs(incomingLogs, countedLogIdsRef.current, windowStart);

      setLogs((current) => {
        return mergeAndSortLogs(incomingLogs, current, windowStart);
      });
      setTotalCalls24h(totalCount);
      setLastSync(formatDate(new Date().toISOString()));
    } catch (err) {
      setError(err?.response?.data?.errorMessage || "No se pudieron cargar los logs recientes.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadOptimizedMonitor]);

  const loadInitialDayLogs = useCallback(async () => {
    const optimizedLoaded = await loadOptimizedMonitor();
    if (optimizedLoaded) return;

    setLoading(true);
    setRefreshing(true);
    setError("");
    setInitialLimitReached(false);

    try {
      const windowStart = getWindowStart();
      const loadedLogs = [];
      countedLogIdsRef.current = new Map();
      let offset = 0;
      let shouldContinue = true;
      let pageCount = 0;

      while (shouldContinue && pageCount < MAX_INITIAL_PAGES) {
        const response = await axios.post(apiUrl(LEGACY_LOGS_PATH), { offset });
        const pageLogs = Array.isArray(response.data?.logs) ? response.data.logs : [];

        if (!pageLogs.length) break;

        loadedLogs.push(...pageLogs.filter((log) => isInsideWindow(log, windowStart)));
        trackCountedLogs(pageLogs, countedLogIdsRef.current, windowStart);

        const reachedOlderLogs = pageLogs.some((log) => !isInsideWindow(log, windowStart));
        shouldContinue = pageLogs.length >= LOG_PAGE_SIZE && !reachedOlderLogs;
        offset += pageLogs.length;
        pageCount += 1;
      }

      if (pageCount >= MAX_INITIAL_PAGES && shouldContinue) {
        setInitialLimitReached(true);
      }

      setLogs(mergeAndSortLogs(loadedLogs, [], windowStart));
      setTotalCalls24h(countedLogIdsRef.current.size);
      setLastSync(formatDate(new Date().toISOString()));
    } catch (err) {
      setError(err?.response?.data?.errorMessage || "No se pudieron cargar los logs de las ultimas 24 horas.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadOptimizedMonitor]);

  useEffect(() => {
    loadInitialDayLogs();
  }, [loadInitialDayLogs]);

  useEffect(() => {
    if (paused) return undefined;

    const interval = setInterval(() => {
      loadRecentLogs(true);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loadRecentLogs, paused]);

  const enrichedLogs = useMemo(() => logs.map((log) => ({
    ...log,
    provider: getProvider(log),
    company: getCompany(log),
    apiName: getApiName(log),
    statusType: getStatusType(log.Status),
  })), [logs]);

  const filteredLogs = useMemo(() => {
    const query = normalizeText(search);

    return enrichedLogs.filter((log) => {
      const providerMatch = filter === "all" || normalizeText(log.provider) === filter;
      const queryMatch = !query || [
        log.ApiLogId,
        log.company,
        log.Email,
        log.apiName,
        log.provider,
        log.Status,
        log.EndPointId,
      ].map(normalizeText).join(" ").includes(query);

      return providerMatch && queryMatch;
    });
  }, [enrichedLogs, filter, search]);

  const summary = useMemo(() => {
    const visibleTotal = enrichedLogs.length;
    const today = Number(serverSummary?.today_calls || 0);
    const success = Number(serverSummary?.success_calls || enrichedLogs.filter((log) => log.statusType === "success").length);
    const timeout = Number(serverSummary?.timeout_calls || enrichedLogs.filter((log) => log.statusType === "timeout").length);
    const rejected = Number(serverSummary?.rejected_calls || enrichedLogs.filter((log) => log.statusType === "rejected").length);
    const errors = Number(serverSummary?.error_calls || enrichedLogs.filter((log) => log.statusType === "error").length);
    const failed = Number(serverSummary?.failed_calls || rejected + timeout + errors);
    const providerCount = new Set(enrichedLogs.map((log) => log.provider)).size;
    const total = Number(serverSummary?.total_calls || visibleTotal);

    return {
      visibleTotal,
      today,
      success,
      timeout,
      rejected,
      errors,
      failed,
      providerCount: Number(serverSummary?.active_providers || providerCount),
      successRate: total ? `${((success * 100) / total).toFixed(1)}%` : "0%",
      failedRate: total ? `${((failed * 100) / total).toFixed(1)}%` : "0%",
    };
  }, [enrichedLogs, serverSummary]);

  const providerStats = useMemo(() => {
    const stats = {};

    if (serverProviders.length) {
      return serverProviders.map((provider) => ({
        provider: provider.provider_name,
        total: Number(provider.total_calls || 0),
        errors: Number(provider.error_calls || 0),
        rejected: Number(provider.rejected_calls || 0),
        timeouts: Number(provider.timeout_calls || 0),
        success: Number(provider.success_calls || 0),
        lastCall: provider.last_call,
      }));
    }

    enrichedLogs.forEach((log) => {
      if (!stats[log.provider]) {
        stats[log.provider] = { provider: log.provider, total: 0, errors: 0, timeouts: 0, success: 0, lastCall: log.CreationDate };
      }

      stats[log.provider].total += 1;
      if (log.statusType === "success") stats[log.provider].success += 1;
      if (log.statusType === "error") stats[log.provider].errors += 1;
      if (log.statusType === "timeout") stats[log.provider].timeouts += 1;
      if (new Date(log.CreationDate || 0) > new Date(stats[log.provider].lastCall || 0)) {
        stats[log.provider].lastCall = log.CreationDate;
      }
    });

    return Object.values(stats).sort((a, b) => b.total - a.total);
  }, [enrichedLogs, serverProviders]);

  const providerOptions = useMemo(() => {
    return providerStats
      .map((provider) => provider.provider)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [providerStats]);

  return (
    <div className="card_list api_card_list api_balances_container live_monitor_container">
      <div className="live_monitor_header">
        <div>
          <h2>Monitor en vivo de APIs</h2>
          <p>Monitoreo liviano de llamadas recientes registradas en base de datos.</p>
        </div>
        <div className="live_monitor_actions">
          <button type="button" onClick={() => setPaused(!paused)} className={paused ? "live_monitor_btn_warning" : ""}>
            {paused ? "Reanudar en vivo" : "Pausar en vivo"}
          </button>
          <button type="button" onClick={() => loadInitialDayLogs()} disabled={refreshing}>
            {refreshing ? "Actualizando..." : "Actualizar ahora"}
          </button>
        </div>
      </div>

      <div className="live_monitor_statusbar">
        <span>{paused ? "En vivo pausado" : "Actualizacion cada minuto"}</span>
        <span>Ventana: ultimas 24 horas</span>
        <span>Ultima sincronizacion: {lastSync || "-"}</span>
      </div>

      {error && <div className="live_monitor_error">{error}</div>}
      {initialLimitReached && <div className="live_monitor_error">Se alcanzo el limite de seguridad de {MAX_INITIAL_PAGES * LOG_PAGE_SIZE} logs para el conteo inicial. Los datos mas recientes siguen visibles.</div>}

      <div className="live_monitor_metrics">
        <MetricCard label="Llamadas del día" value={summary.today} detail="Desde las 00:00 de Ciudad de México" />
        <MetricCard label="Llamadas últimas 24 horas" value={totalCalls24h} detail="Ventana móvil sin excluir status" />
        <MetricCard label="Exitosas" value={summary.success} detail={`${summary.successRate} del total`} tone="success" />
        <MetricCard label="Fallidas totales" value={summary.failed} detail={`${summary.failedRate} entre 4xx, timeout y tecnicas`} tone="error" />
        <MetricCard label="Rechazadas 4xx" value={summary.rejected} detail="Validacion, datos o limite" tone="rejected" />
        <MetricCard label="Timeouts" value={summary.timeout} detail="Detectados por status" tone="timeout" />
        <MetricCard label="Errores tecnicos" value={summary.errors} detail="Fallas de API o proveedor" tone="error" />
        <MetricCard label="Proveedores" value={summary.providerCount} detail="Detectados desde logs" />
      </div>

      <div className="live_monitor_filters">
        <input
          type="text"
          value={search}
          placeholder="Buscar por empresa, email, API, proveedor, status o log id"
          onChange={(event) => setSearch(event.target.value)}
        />
        <select value={filter} onChange={(event) => setFilter(event.target.value)}>
          <option value="all">Todos los proveedores</option>
          {providerOptions.map((provider) => (
            <option className="notranslate" value={normalizeText(provider)} key={provider}>{provider}</option>
          ))}
        </select>
      </div>

      <div className="live_monitor_grid">
        <div className="live_monitor_panel live_monitor_provider_panel">
          <div className="live_monitor_panel_header">
            <h3>Salud por proveedor</h3>
            <span>Distribucion de trafico por proveedor detectado</span>
          </div>
          <div className="live_monitor_provider_list">
            {providerStats.length ? providerStats.map((provider) => {
              const errorTotal = provider.errors + provider.rejected + provider.timeouts;
              const successRate = provider.total ? ((provider.success * 100) / provider.total).toFixed(1) : "0.0";

              return (
                <div className="live_monitor_provider" key={provider.provider}>
                  <div>
                    <strong className="notranslate" translate="no">{provider.provider}</strong>
                    <span>{provider.total} llamadas - {successRate}% exito</span>
                  </div>
                  <small>{errorTotal} problemas - ultima {formatDate(provider.lastCall)}</small>
                </div>
              );
            }) : <div className="live_monitor_empty">Todavia no hay trafico de proveedores.</div>}
          </div>
        </div>

        <div className="live_monitor_panel live_monitor_diagnostic_panel">
          <div className="live_monitor_panel_header">
            <h3>Diagnostico de fallas</h3>
            <span>Status y APIs con mayor actividad</span>
          </div>
          <div className="live_monitor_diagnostics">
            <div>
              <strong>Status del dia</strong>
              {serverStatusBreakdown.length ? serverStatusBreakdown.map((item) => (
                <div className="live_monitor_diag_row" key={`${item.status}-${item.categoria}`}>
                  <span className="notranslate" translate="no">{item.status}</span>
                  <small>{item.categoria} - {item.total_calls} llamadas</small>
                </div>
              )) : <div className="live_monitor_empty">Sin desglose de status.</div>}
            </div>
            <div>
              <strong>APIs</strong>
              {serverEndpoints.length ? serverEndpoints.slice(0, 8).map((item) => (
                <div className="live_monitor_diag_row" key={item.EndPointId || item.endpoint_name}>
                  <span className="notranslate" translate="no">{item.api_name || "Sin API"}</span>
                  <small className="notranslate" translate="no">{item.endpoint_path || item.EndPointId || "-"} - {item.total_calls} llamadas - {item.failed_calls || 0} fallidas</small>
                </div>
              )) : <div className="live_monitor_empty">Sin desglose de APIs.</div>}
            </div>
          </div>
        </div>

        <div className="live_monitor_panel live_monitor_table_panel">
          <div className="live_monitor_panel_header">
            <h3>Flujo de llamadas 24h</h3>
            <span>{filteredLogs.length} eventos visibles</span>
          </div>

          {loading ? (
            <>
              <div className="task_card_skeleton"></div>
              <div className="task_card_skeleton"></div>
            </>
          ) : (
            <div className="live_monitor_table_wrap">
              <table className="table_balances live_monitor_table">
                <thead>
                  <tr className="table_balances_row">
                    <th className="table_balances_item table_balances_item_4 table_balances_item_init_width">Hora</th>
                    <th className="table_balances_item table_balances_item_4 table_balances_item_init_width">Proveedor</th>
                    <th className="table_balances_item table_balances_item_4 table_balances_item_init_width">API</th>
                    <th className="table_balances_item table_balances_item_4 table_balances_item_init_width">Empresa</th>
                    <th className="table_balances_item table_balances_item_4 table_balances_item_init_width">Correo</th>
                    <th className="table_balances_item table_balances_item_4 table_balances_item_init_width">Estado</th>
                    <th className="table_balances_item table_balances_item_4 table_balances_item_init_width">ID log</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr className="table_balances_row" key={getLogId(log)}>
                      <td className="table_balances_item table_balances_item_init_width table_balances_item_init_nowrap">{formatDate(log.CreationDate)}</td>
                      <td className="table_balances_item table_balances_item_init_width notranslate" translate="no">{log.provider}</td>
                      <td className="table_balances_item table_balances_item_init_width notranslate" translate="no">{log.apiName}</td>
                      <td className="table_balances_item table_balances_item_init_width notranslate" translate="no">{log.company}</td>
                      <td className="table_balances_item table_balances_item_init_width notranslate" translate="no">{log.Email || "-"}</td>
                      <td className="table_balances_item table_balances_item_init_width">
                        <span className={`live_monitor_badge notranslate ${log.statusType}`} translate="no">{log.Status || "-"}</span>
                      </td>
                      <td className="table_balances_item table_balances_item_init_width">{log.ApiLogId || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!filteredLogs.length && <div className="live_monitor_empty">No hay logs para los filtros seleccionados.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminLiveMonitor;
